import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Dark mode readability: disabled buttons use opacity-70 (not 50) to remain legible
  // against the dark background. Light mode keeps opacity-50 unchanged.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 dark:disabled:opacity-70 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        /* Dark mode fix: bg-background matches the page body in dark theme so the button
           would disappear. bg-card/bg-popover would collide with Dialog bodies (same HSL).
           bg-muted (200 30% 15%) is 9% lighter than page body AND 5% lighter than Dialog
           popover — visible in BOTH contexts. Light mode unchanged. */
        outline:
          'border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground dark:bg-muted dark:border-border/80 dark:hover:bg-accent/80',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        /* Dark mode fix: ghost had no explicit text color, inherited from context — often
           low contrast in dark containers. Explicit text-foreground guarantees legibility. */
        ghost:
          'text-foreground hover:bg-accent hover:text-accent-foreground dark:text-foreground/90',
        link: 'text-primary underline-offset-4 hover:underline',
        /* ADR-070: CTA Orange — only for primary action buttons */
        cta: 'btn-cta rounded-md text-white font-semibold',
        /* ADR-070: 3D elevated button with specular highlight */
        '3d': 'btn-3d bg-primary text-primary-foreground',
        /* ADR-070: 3D destructive */
        '3d-destructive': 'btn-3d bg-destructive text-destructive-foreground',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
